/**
 * **Two markers, A and B, on a row of instants** — how they move, whichever gesture asks.
 *
 * Promoted out of `pages/client-detail/model/twoDates` on 28 September 2026, on its second consumer:
 * the plan overview's week comparison draws the same A and B as the Pomiary and Forma tabs, and the
 * owner asked for them to BE the same, grip and rules alike. The rules moved unchanged: a marker
 * snaps to the nearest instant, never crosses the other and never lands on it, and A is always the
 * earlier. `shared/ui/chart/SelectionMarkers` draws them.
 */

export type MarkerKey = 'a' | 'b';

/** The two markers, as instants on the row. `a` is strictly before `b`. */
export interface MarkerSelection {
  readonly a: number;
  readonly b: number;
}

/**
 * Every way a marker moves on the plot, as one operation — so the rules that make a move legal are
 * the same whichever gesture asked for it.
 */
export type MarkerMove =
  /** A drag or a menu choice: this marker, to the instant nearest this one. */
  | { readonly kind: 'drag'; readonly marker: MarkerKey; readonly at: number }
  /** An arrow key: this marker, this many instants along. */
  | { readonly kind: 'step'; readonly marker: MarkerKey; readonly by: number }
  /** Home and End. */
  | { readonly kind: 'end'; readonly marker: MarkerKey; readonly to: 'first' | 'last' }
  /** A click on the plot: the NEARER marker moves to the instant nearest this one. */
  | { readonly kind: 'pick'; readonly at: number };

/** One marker as its handle draws and names it. */
export interface SelectionMarkerView {
  readonly key: MarkerKey;
  /** Its position on the row, which is what `aria-valuenow` reports. */
  readonly index: number;
  readonly min: number;
  readonly max: number;
  readonly at: Date;
  /** For `aria-valuetext` — „16 września, 64,2 kg", „tydzień 4, 10,8 t". */
  readonly valueText: string;
}

/** The position of the instant on the row nearest `at`. */
export function nearestIndex(times: readonly number[], at: number): number {
  let best = 0;
  let distance = Number.POSITIVE_INFINITY;
  for (const [index, time] of times.entries()) {
    const gap = Math.abs(time - at);
    if (gap < distance) {
      distance = gap;
      best = index;
    }
  }
  return best;
}

/**
 * Where a move leaves the pair. `times` is oldest first. A move that would cross or collapse onto
 * the other marker leaves the pair where it was.
 */
export function moveOnTimes(
  times: readonly number[],
  current: MarkerSelection | null,
  move: MarkerMove,
): MarkerSelection | null {
  if (current === null || times.length < 2) return current;

  const indexA = nearestIndex(times, current.a);
  const indexB = nearestIndex(times, current.b);

  const marker: MarkerKey =
    move.kind === 'pick'
      ? Math.abs(move.at - current.a) <= Math.abs(move.at - current.b)
        ? 'a'
        : 'b'
      : move.marker;

  const target =
    move.kind === 'step'
      ? (marker === 'a' ? indexA : indexB) + move.by
      : move.kind === 'end'
        ? move.to === 'first'
          ? 0
          : times.length - 1
        : nearestIndex(times, move.at);

  // Never past the ends, and never onto or across the other marker.
  const bounded =
    marker === 'a' ? Math.min(target, indexB - 1) : Math.max(target, indexA + 1);
  const clamped = Math.max(0, Math.min(times.length - 1, bounded));
  if (marker === 'a' && clamped >= indexB) return current;
  if (marker === 'b' && clamped <= indexA) return current;

  return marker === 'a'
    ? { a: times[clamped] as number, b: current.b }
    : { a: current.a, b: times[clamped] as number };
}
